/**
 * Per-user fib level configuration. Loaded from `/api/fib_levels` on
 * app start and persisted back on save. The defaults baked in here
 * mirror the server's `DEFAULT_FIB_LEVELS` so the UI works while the
 * fetch is in flight.
 *
 * `lineWidth` + `lineStyle` are SHARED across every level (one
 * stroke style for all). Per-level color stays in each FibLevel.
 * Stored in this slice alongside the levels so the User-scope
 * round-trip via /api/fib_levels covers everything in one PUT.
 */
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { api } from "@/api/client";
import type {
  FibDiagonalConfig,
  FibLevel,
  FibLevelsPayload,
} from "@/api/types";
import type { LineStyleName } from "./uiSlice";

interface FibLevelsState {
  levels: FibLevel[];
  /** Shared stroke for every fib level. 1..4 px. */
  lineWidth: number;
  /** Shared stroke style. Maps 1:1 to lightweight-charts
   *  LineStyle at render time. */
  lineStyle: LineStyleName;
  /** Connecting diagonal/trend line appearance — its OWN color +
   *  width + style (no longer the shared level stroke + hardcoded
   *  grey). */
  diagonal: FibDiagonalConfig;
  /** Default diagonal visibility. A per-fib override can hide it. */
  showDiagonal: boolean;
  /** Default side for the level labels. */
  labelSide: "left" | "right";
  status: "idle" | "loading" | "succeeded" | "failed";
  saving: boolean;
  error: string | null;
}

export const DEFAULT_LEVELS: FibLevel[] = [
  { ratio: 0.0, color: "#787B86", enabled: true, opacity: 1 },
  { ratio: 0.236, color: "#F23645", enabled: true, opacity: 1 },
  { ratio: 0.382, color: "#FF9800", enabled: true, opacity: 1 },
  { ratio: 0.5, color: "#26A69A", enabled: true, opacity: 1 },
  { ratio: 0.618, color: "#FF9800", enabled: true, opacity: 1 },
  { ratio: 1.0, color: "#787B86", enabled: true, opacity: 1 },
];

export const DEFAULT_FIB_LINE_WIDTH = 1;
export const DEFAULT_FIB_LINE_STYLE: LineStyleName = "solid";
// Matches the previous hard-coded diagonal (grey, 1px, solid).
export const DEFAULT_FIB_DIAGONAL: FibDiagonalConfig = {
  color: "#888888",
  width: 1,
  style: "solid",
};
export const DEFAULT_FIB_SHOW_DIAGONAL = true;
export const DEFAULT_FIB_LABEL_SIDE: "left" | "right" = "right";

const initialState: FibLevelsState = {
  levels: DEFAULT_LEVELS,
  lineWidth: DEFAULT_FIB_LINE_WIDTH,
  lineStyle: DEFAULT_FIB_LINE_STYLE,
  diagonal: DEFAULT_FIB_DIAGONAL,
  showDiagonal: DEFAULT_FIB_SHOW_DIAGONAL,
  labelSide: DEFAULT_FIB_LABEL_SIDE,
  status: "idle",
  saving: false,
  error: null,
};

/** Merge any diagonal/show/label fields from a fetched/saved payload
 *  into state (each optional for back-compat with older saves). */
function applyFibExtras(
  state: FibLevelsState,
  p: FibLevelsPayload,
): void {
  if (typeof p.lineWidth === "number") state.lineWidth = p.lineWidth;
  if (p.lineStyle) state.lineStyle = p.lineStyle as LineStyleName;
  if (p.diagonal) state.diagonal = p.diagonal;
  if (typeof p.showDiagonal === "boolean") state.showDiagonal = p.showDiagonal;
  if (p.labelSide) state.labelSide = p.labelSide;
}

export const fetchFibLevels = createAsyncThunk(
  "fibLevels/fetch",
  async () => api.getFibLevels(),
);

interface SaveFibArgs {
  levels: FibLevel[];
  lineWidth: number;
  lineStyle: LineStyleName;
  diagonal: FibDiagonalConfig;
  showDiagonal: boolean;
  labelSide: "left" | "right";
}

export const saveFibLevels = createAsyncThunk<
  FibLevelsPayload,
  SaveFibArgs
>("fibLevels/save", async (args) => api.saveFibLevels(args));

const fibLevelsSlice = createSlice({
  name: "fibLevels",
  initialState,
  reducers: {
    /** Local-only update — used by the dialog's draft state. Not
     * persisted until `saveFibLevels` is dispatched. */
    setFibLevels(state, action: PayloadAction<FibLevel[]>) {
      state.levels = action.payload;
    },
    setFibLineWidth(state, action: PayloadAction<number>) {
      state.lineWidth = action.payload;
    },
    setFibLineStyle(state, action: PayloadAction<LineStyleName>) {
      state.lineStyle = action.payload;
    },
    setFibDiagonal(state, action: PayloadAction<FibDiagonalConfig>) {
      state.diagonal = action.payload;
    },
    setFibShowDiagonal(state, action: PayloadAction<boolean>) {
      state.showDiagonal = action.payload;
    },
    setFibLabelSide(state, action: PayloadAction<"left" | "right">) {
      state.labelSide = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFibLevels.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchFibLevels.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.levels = action.payload.levels;
        applyFibExtras(state, action.payload);
      })
      .addCase(fetchFibLevels.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load fib levels";
      })
      .addCase(saveFibLevels.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveFibLevels.fulfilled, (state, action) => {
        state.saving = false;
        state.levels = action.payload.levels;
        applyFibExtras(state, action.payload);
      })
      .addCase(saveFibLevels.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? "Failed to save fib levels";
      });
  },
});

export const {
  setFibLevels,
  setFibLineWidth,
  setFibLineStyle,
  setFibDiagonal,
  setFibShowDiagonal,
  setFibLabelSide,
} = fibLevelsSlice.actions;
export default fibLevelsSlice.reducer;
